/**
 * How an owner-membership result reads to a human.
 *
 * Pure, and separate from the card, because the honest-reporting rule lives
 * here: a channel that WAS created with two owners missing must not read as an
 * unqualified success. The failures are named, with the reason the API gave, and
 * the reader is told what to do about it.
 */

/** One owner the Slack invite did not reach. */
export interface OwnerMembershipFailure {
  email: string;
  reason: string;
}

/** The `membership` block the channel endpoints return, as far as we read it. */
export interface OwnerMembershipResult {
  failures?: OwnerMembershipFailure[];
}

/** Narrow an unknown JSON body's `membership.failures` without trusting it. */
export function readMembershipFailures(body: unknown): OwnerMembershipFailure[] {
  if (typeof body !== "object" || body === null) return [];
  const { membership } = body as { membership?: unknown };
  if (typeof membership !== "object" || membership === null) return [];
  const { failures } = membership as { failures?: unknown };
  if (!Array.isArray(failures)) return [];
  return failures.filter(
    (failure): failure is OwnerMembershipFailure =>
      typeof failure === "object" &&
      failure !== null &&
      typeof (failure as OwnerMembershipFailure).email === "string" &&
      typeof (failure as OwnerMembershipFailure).reason === "string"
  );
}

/** Format an owner-membership result into a user-facing message. */
export function membershipMessage(
  base: string,
  failures: readonly OwnerMembershipFailure[]
): string {
  if (failures.length > 0) {
    return `${base} ⚠️ Couldn't add: ${failures
      .map((f) => `${f.email} (${f.reason})`)
      .join(", ")}. Add them to the channel manually in Slack.`;
  }
  return `${base} Owners (Eliza, Charlie, Miclain) are in the channel.`;
}

/** Headline for a create response — the channel may already have existed. */
export function createChannelHeadline(alreadyExisted: boolean): string {
  return alreadyExisted
    ? "This deal already has a project channel — owner membership re-checked."
    : "Project channel created.";
}

/**
 * The confirmation text for unlinking.
 *
 * NEVER_DELETE_SLACK_CHANNEL: "unlink/reset" clears Hub's reference only, and
 * the prompt has to say so — a reader who thinks this archives the channel will
 * not click it, and a reader who thinks it does and clicks anyway was misled.
 */
export const UNLINK_CONFIRM_TEXT =
  "Unlink this project channel from the deal? The Slack channel stays in Slack, but the chat link is reset and you can create a fresh project channel afterward.";
